import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import { 
  collection, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  serverTimestamp,
  where,
  updateDoc
} from "firebase/firestore";
import { 
  LuDollarSign, 
  LuTrash2, 
  LuArrowLeft, 
  LuHistory,
  LuLoader,
  LuSave,
  LuWallet,
  LuPencil,
  LuX
} from "react-icons/lu";
import { useNotification } from "../../common/NotificationContext";
import { InputField } from "@cakrawalajakarta/shared-components";

export const Finance = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    grossRevenue: 0,
    midtransFees: 0,
    netRevenue: 0,
    eventGrossRevenue: 0,
    eventNetRevenue: 0,
    shuttleGrossRevenue: 0,
    shuttleNetRevenue: 0,
    hotelGrossRevenue: 0,
    hotelNetRevenue: 0,
    funRunGrossRevenue: 0,
    funRunNetRevenue: 0,
    totalWithdrawals: 0,
    balance: 0
  });
  const [withdrawals, setWithdrawals] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    amount: "",
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5)
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // 1. Fetch Revenue data (Event + Shuttle + Hotel + Fun Run + Category Upgrade)
      const participantsRef = collection(db, "pendaftaranJOTR2026");
      const shuttleRef = collection(db, "pendaftaranShuttle2026");
      const hotelRef = collection(db, "pendaftaranHotel2026");
      const funRunRef = collection(db, "pendaftaranFunRun5K2026");
      const categoryUpgradeRef = collection(db, "categoryTransferPayments");
      const [participantsSnapshot, shuttleSnapshot, hotelSnapshot, funRunSnapshot, categoryUpgradeSnapshot] = await Promise.all([
        getDocs(participantsRef),
        getDocs(query(shuttleRef, where("transaction_status", "in", ["settlement", "capture"]))),
        getDocs(query(hotelRef, where("transaction_status", "in", ["settlement", "capture"]))),
        getDocs(query(funRunRef, where("transaction_status", "in", ["settlement", "capture"]))),
        getDocs(query(categoryUpgradeRef, where("eventId", "==", "JOTR2026"), where("transaction_status", "in", ["settlement", "capture"])))
      ]);

      let eventGross = 0;
      let eventNet = 0;
      let eventFees = 0;

      participantsSnapshot.forEach((doc) => {
        const p = doc.data();
        const isSettled = p.transaction_status === "settlement" || p.transaction_status === "capture";
        const isPayPalCompleted = p.paypalCapture?.status === "COMPLETED";
        const isFree = p.payment_type === "free" || p.payment_type === "free_voucher" || Number(p.gross_amount) === 0 || Number(p.harga) === 0 || !p.harga;

        if ((isSettled || isPayPalCompleted) && !isFree) {
          const isPayPal = isPayPalCompleted || p.currency === "USD";
          if (!isPayPal) {
            const amountIDR = Number(p.gross_amount) || Number(p.harga) || 0;
            const midtransFee = 4440;
            eventGross += amountIDR;
            eventFees += midtransFee;
            eventNet += Math.max(0, amountIDR - midtransFee);
          }
        }
      });

      const getRevenueAmount = (data) =>
        Number(data.totalHarga || data.harga || data.gross_amount) || 0;
      const isFinanceRevenue = (data) =>
        !data.financeExcluded && getRevenueAmount(data) > 0;

      const shuttleRevenueDocs = shuttleSnapshot.docs.filter((doc) =>
        isFinanceRevenue(doc.data())
      );
      const hotelRevenueDocs = hotelSnapshot.docs.filter((doc) =>
        isFinanceRevenue(doc.data())
      );
      const funRunRevenueDocs = funRunSnapshot.docs.filter((doc) =>
        isFinanceRevenue(doc.data())
      );
      const categoryUpgradeRevenueDocs = categoryUpgradeSnapshot.docs.filter((doc) =>
        isFinanceRevenue(doc.data())
      );

      const shuttleGross = shuttleRevenueDocs.reduce(
        (sum, doc) => sum + getRevenueAmount(doc.data()),
        0
      );
      const shuttleFees = shuttleRevenueDocs.length * 4440;
      const shuttleNet = Math.max(0, shuttleGross - shuttleFees);

      const hotelGross = hotelRevenueDocs.reduce(
        (sum, doc) => sum + getRevenueAmount(doc.data()),
        0
      );
      const hotelFees = hotelRevenueDocs.length * 4440;
      const hotelNet = Math.max(0, hotelGross - hotelFees);

      const funRunGross = funRunRevenueDocs.reduce(
        (sum, doc) => sum + getRevenueAmount(doc.data()),
        0
      );
      const funRunFees = funRunRevenueDocs.length * 4440;
      const funRunNet = Math.max(0, funRunGross - funRunFees);

      const categoryUpgradeGross = categoryUpgradeRevenueDocs.reduce(
        (sum, doc) => sum + getRevenueAmount(doc.data()),
        0
      );
      const categoryUpgradeFees = categoryUpgradeRevenueDocs.length * 4440;
      const categoryUpgradeNet = Math.max(0, categoryUpgradeGross - categoryUpgradeFees);

      const gross = eventGross + shuttleGross + hotelGross + funRunGross + categoryUpgradeGross;
      const fees = eventFees + shuttleFees + hotelFees + funRunFees + categoryUpgradeFees;
      const net = eventNet + shuttleNet + hotelNet + funRunNet + categoryUpgradeNet;

      // 2. Fetch Withdrawals
      const wQuery = query(collection(db, "withdrawals"), where("eventId", "==", "JOTR2026"), orderBy("date", "desc"));
      const wSnapshot = await getDocs(wQuery);
      const wList = wSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const totalW = wList.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);

      setWithdrawals(wList);
      setStats({
        grossRevenue: gross,
        midtransFees: fees,
        netRevenue: net,
        eventGrossRevenue: eventGross,
        eventNetRevenue: eventNet,
        shuttleGrossRevenue: shuttleGross,
        shuttleNetRevenue: shuttleNet,
        hotelGrossRevenue: hotelGross,
        hotelNetRevenue: hotelNet,
        funRunGrossRevenue: funRunGross,
        funRunNetRevenue: funRunNet,
        totalWithdrawals: totalW,
        balance: net - totalW
      });
    } catch (error) {
      console.error("Error fetching finance data:", error);
      showNotification("error", "Failed to load finance data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || Number(formData.amount) <= 0) {
      showNotification("error", "Please enter a valid amount");
      return;
    }

    setIsSaving(true);
    try {
      const dataToSave = {
        amount: Number(formData.amount),
        date: formData.date,
        time: formData.time,
        eventId: "JOTR2026",
        updatedAt: serverTimestamp()
      };

      if (isEditing) {
        await updateDoc(doc(db, "withdrawals", editingId), dataToSave);
        showNotification("success", "Withdrawal updated successfully");
      } else {
        await addDoc(collection(db, "withdrawals"), {
          ...dataToSave,
          createdAt: serverTimestamp()
        });
        showNotification("success", "Withdrawal added successfully");
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving withdrawal:", error);
      showNotification("error", "Failed to save withdrawal");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (w) => {
    setIsEditing(true);
    setEditingId(w.id);
    setFormData({
      amount: w.amount.toString(),
      date: w.date,
      time: w.time || "00:00"
    });
    // Scroll to top or form if needed
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      amount: "",
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5)
    });
  };

  const handleDeleteWithdrawal = async (id) => {
    if (!confirm("Are you sure you want to delete this withdrawal entry?")) return;

    try {
      await deleteDoc(doc(db, "withdrawals", id));
      showNotification("success", "Withdrawal deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting withdrawal:", error);
      showNotification("error", "Failed to delete withdrawal");
    }
  };

  const formatIDR = (val) => `Rp ${(Number(val) || 0).toLocaleString('en-US')}`;

  if (loading && !stats.netRevenue) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-slate-800" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-10">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin")}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors text-slate-600"
          >
            <LuArrowLeft size={18} />
          </button>
          <h1 className="text-2xl font-bold text-slate-900">Finance & Balance</h1>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Main Balance Card */}
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Total Balance</p>
              <p className="text-xl font-bold text-slate-800">{formatIDR(stats.balance)}</p>
            </div>
            <LuWallet size={24} className="text-slate-300" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Net Revenue</p>
              <p className="text-xl font-bold text-slate-800">{formatIDR(stats.netRevenue)}</p>
            </div>
            <LuDollarSign size={24} className="text-slate-300" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Total Withdrawals</p>
              <p className="text-xl font-bold text-slate-800">{formatIDR(stats.totalWithdrawals)}</p>
            </div>
            <LuHistory size={24} className="text-slate-300" />
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-sm font-medium mb-1">Event Revenue</p>
          <p className="text-xl font-bold text-slate-800">{formatIDR(stats.eventGrossRevenue)}</p>
          <p className="text-sm text-slate-400 mt-1">Net: {formatIDR(stats.eventNetRevenue)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-sm font-medium mb-1">Shuttle Revenue</p>
          <p className="text-xl font-bold text-slate-800">{formatIDR(stats.shuttleGrossRevenue)}</p>
          <p className="text-sm text-slate-400 mt-1">Net: {formatIDR(stats.shuttleNetRevenue)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-sm font-medium mb-1">Hotel Revenue</p>
          <p className="text-xl font-bold text-slate-800">{formatIDR(stats.hotelGrossRevenue)}</p>
          <p className="text-sm text-slate-400 mt-1">Net: {formatIDR(stats.hotelNetRevenue)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-sm font-medium mb-1">Fun Run 5K Revenue</p>
          <p className="text-xl font-bold text-slate-800">{formatIDR(stats.funRunGrossRevenue)}</p>
          <p className="text-sm text-slate-400 mt-1">Net: {formatIDR(stats.funRunNetRevenue)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Withdrawal Form */}
        <div className="lg:col-span-1 space-y-4">
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-2">
              <h3 className="font-bold text-slate-800">{isEditing ? "Edit Withdrawal" : "Record Withdrawal"}</h3>
              {isEditing && (
                <button type="button" onClick={resetForm} className="text-slate-400 hover:text-slate-600">
                  <LuX size={18} />
                </button>
              )}
            </div>
            
            <div className="space-y-4">
              <InputField
                label="Amount (IDR)"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="e.g. 5000000"
                required
                labelColor="text-slate-700"
              />
              
              <div className="grid grid-cols-2 gap-2">
                <InputField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  labelColor="text-slate-700"
                />
                <InputField
                  label="Time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                  labelColor="text-slate-700"
                />
              </div>

              <button
                type="submit"
                disabled={isSaving}
                className={`w-full py-2 ${isEditing ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-800 hover:bg-slate-900"} text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2`}
              >
                {isSaving ? <LuLoader className="animate-spin" size={16} /> : <LuSave size={16} />}
                {isSaving ? "Saving..." : (isEditing ? "Update Withdrawal" : "Save Withdrawal")}
              </button>
            </div>
          </form>
        </div>

        {/* Withdrawal History List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden h-full">
            <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-bold text-slate-800">Withdrawal History</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-slate-500">
                <thead className="bg-slate-50 text-slate-600 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Date & Time</th>
                    <th className="px-4 py-3 font-semibold text-right">Amount</th>
                    <th className="px-4 py-3 font-semibold text-center">Action</th>
                  </tr>
                </thead>
              <tbody className="divide-y divide-slate-100">
  {withdrawals.length === 0 ? (
    <tr>
      <td colSpan={3} className="p-8 text-center text-slate-400 italic">
        No withdrawal history found.
      </td>
    </tr>
  ) : (
    withdrawals.map((w) => {
      // Format tanggal + jam jadi satu string
      const dateTimeStr = w.date 
        ? new Date(`${w.date}T${w.time || "00:00"}`)
            .toLocaleString('en-US', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
            .replace(',', '') // remove comma if any
        : "—";

      return (
        <tr 
          key={w.id} 
          className={`hover:bg-slate-50 ${editingId === w.id ? "bg-blue-50" : ""}`}
        >
          <td className="px-4 py-3 font-medium text-slate-800">
            {dateTimeStr}
          </td>
          <td className="px-4 py-3 text-right font-bold text-slate-800">
            {formatIDR(w.amount)}
          </td>
          <td className="px-4 py-3 text-center">
            <div className="flex items-center justify-center gap-1">
              <button 
                onClick={() => handleEdit(w)}
                className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                title="Edit"
              >
                <LuPencil size={16} />
              </button>
              <button 
                onClick={() => handleDeleteWithdrawal(w.id)}
                className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                title="Delete"
              >
                <LuTrash2 size={16} />
              </button>
            </div>
          </td>
        </tr>
      );
    })
  )}
</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
